/**
 * Privacy helpers for analytics (Umami + GA4 dual-send).
 *
 * Two guarantees live here so both the tracker config (layout) and the
 * event wrapper (analytics.js) share one definition:
 *
 * 1. Inquiry access tokens (`?token=...`) never leave the browser in
 *    analytics payloads. Tokens are bearer credentials — anyone with
 *    dashboard access could otherwise replay an unexpired one.
 * 2. Session replay never starts on sensitive routes (`/inquiries/*`,
 *    `/admin/*`), where page content itself is private.
 */

// Query params treated as credentials and stripped from tracked URLs.
export const SENSITIVE_ANALYTICS_PARAMS = ['token', 'access_token', '_token'];

// Route prefixes where session replay must not run. Normalized, no
// trailing slash. Inquiry pages carry bearer tokens; admin pages show
// customer PII and internal tooling.
export const SENSITIVE_REPLAY_PATH_PREFIXES = ['/inquiries', '/admin'];

// CSS hook class applied to sensitive page shells. Pair with the Umami
// dashboard setting Replays -> Block Selector = `.umami-block` so the
// recorder drops this subtree even if it is already running (e.g. after
// client-side navigation into a sensitive route).
export const SENSITIVE_REPLAY_BLOCK_CLASS = 'umami-block';

/**
 * Strip query/hash noise for route comparisons.
 */
export function normalizePathname(pathname) {
    if (typeof pathname !== 'string' || pathname.length === 0) return '';
    return pathname.split('?')[0].split('#')[0];
}

/**
 * True when the recorder must stay off this route.
 */
export function isSensitiveReplayPath(pathname) {
    const normalized = normalizePathname(pathname);
    if (!normalized) return false;
    return SENSITIVE_REPLAY_PATH_PREFIXES.some(
        (prefix) =>
            normalized === prefix || normalized.startsWith(`${prefix}/`),
    );
}

/**
 * Remove credential params from a tracked URL, preserving everything
 * else (including UTM params used for campaign attribution).
 * Returns the original value untouched when there is nothing to redact
 * or the value is not parseable. Output is path + search + hash, which
 * is the shape Umami accepts for `payload.url`.
 */
export function redactSensitiveParams(value) {
    if (typeof value !== 'string' || value.length === 0) return value;
    if (!value.includes('token')) return value;
    try {
        const url = new URL(value, 'https://redact.internal');
        let changed = false;
        for (const key of SENSITIVE_ANALYTICS_PARAMS) {
            if (url.searchParams.has(key)) {
                url.searchParams.delete(key);
                changed = true;
            }
        }
        if (!changed) return value;
        const search = url.searchParams.toString();
        return url.pathname + (search ? `?${search}` : '') + url.hash;
    } catch (_e) {
        return value;
    }
}

/**
 * Shallow-clone an event-props object minus credential keys.
 * Defense in depth for `umami.track(name, params)` call-sites.
 */
export function stripSensitiveParamKeys(params) {
    if (!params || typeof params !== 'object' || Array.isArray(params)) {
        return params;
    }
    const cleaned = { ...params };
    for (const key of SENSITIVE_ANALYTICS_PARAMS) {
        delete cleaned[key];
    }
    return cleaned;
}
